import React, { useState, useEffect } from 'react';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import ConfirmDialog from '../Components/ConfirmDialog';

const OrderTracking = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [activeTrackingId, setActiveTrackingId] = useState(null);

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    variant: 'default'
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await HttpInterceptor.get('/api/orders');
      if (response.data?.success && response.data?.data) {
        setOrders(response.data.data);
      } else {
        setOrders([]);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load orders';

      // If unauthorized, show a more helpful message
      if (error.response?.status === 401) {
        toast.error('Please log in to view your orders');
      } else if (error.response?.status === 400) {
        toast.error(errorMessage || 'Invalid request. Please try again.');
      } else {
        toast.error(errorMessage);
      }

      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDelivered = async (orderId) => {
    try {
      await HttpInterceptor.post(`/api/orders/${orderId}/delivered`);
      toast.success('Order marked as delivered');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to mark order as delivered');
    }
  };

  const executeDeleteOrder = async (checkoutId) => {
    try {
      await HttpInterceptor.patch(`/api/orders/group/${checkoutId}/hide`);
      toast.success('Order removed from history');
      fetchOrders();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to remove order');
    }
  };

  const handleDeleteOrder = (checkoutId) => {
    if (!checkoutId) return;
    setConfirmConfig({
      isOpen: true,
      title: 'Remove Order',
      message: 'Are you sure you want to remove this order from your view? This action cannot be undone.',
      onConfirm: () => executeDeleteOrder(checkoutId),
      variant: 'danger'
    });
  };

  const executeCancelOrder = async (orderId) => {
    try {
      await HttpInterceptor.post(`/api/orders/${orderId}/cancel`);
      toast.success('Order cancelled successfully');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel order');
    }
  };

  const handleCancelOrder = (orderId) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Cancel Order',
      message: `Are you sure you want to cancel Order #${orderId.slice(-8).toUpperCase()}? This action cannot be undone.`,
      onConfirm: () => executeCancelOrder(orderId),
      variant: 'danger'
    });
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size should be less than 2MB');
      return;
    }

    setUploadingReceipt(true);
    const formData = new FormData();
    formData.append('receipt', file);

    try {
      await HttpInterceptor.post(`/api/payments/${selectedOrderId}/receipt`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Receipt uploaded successfully! Admins will verify it shortly.');
      setShowReceiptModal(false);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to upload receipt');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'text-green-600 bg-green-100';
      case 'shipped': return 'text-blue-600 bg-blue-100';
      case 'processing': return 'text-yellow-600 bg-yellow-100';
      case 'confirmed': return 'text-purple-600 bg-purple-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'refunded': return 'text-orange-600 bg-orange-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const TrackingProgress = ({ status }) => {
    const steps = [
      { id: 'pending', label: 'Order Placed', icon: '📝' },
      { id: 'confirmed', label: 'Confirmed', icon: '✅' },
      { id: 'processing', label: 'Processing', icon: '⚙️' },
      { id: 'shipped', label: 'Shipped', icon: '🚚' },
      { id: 'delivered', label: 'Delivered', icon: '🎁' }
    ];

    const currentIdx = steps.findIndex(s => s.id === status);
    const activeIdx = status === 'cancelled' ? -1 : currentIdx;

    return (
      <div className="mt-6 mb-4">
        <div className="flex items-center justify-between relative px-2">
          {/* Progress Line */}
          <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-200 -translate-y-1/2 z-0"></div>
          <div 
            className="absolute top-1/2 left-0 h-1 bg-purple-500 -translate-y-1/2 z-0 transition-all duration-700 ease-in-out"
            style={{ width: activeIdx >= 0 ? `${(activeIdx / (steps.length - 1)) * 100}%` : '0%' }}
          ></div>

          {/* Step Dots */}
          {steps.map((step, idx) => (
            <div key={step.id} className="relative z-10 flex flex-col items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs transition-all duration-300 ${
                  idx <= activeIdx ? 'bg-purple-600 text-white shadow-lg scale-110' : 'bg-white border-2 border-gray-200 text-gray-400'
                }`}
                title={step.label}
              >
                {idx <= activeIdx ? '✓' : step.icon}
              </div>
              <span className={`absolute -bottom-6 text-[9px] font-bold uppercase tracking-tighter whitespace-nowrap ${
                idx <= activeIdx ? 'text-purple-700' : 'text-gray-400'
              }`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>
        {status === 'cancelled' && (
          <div className="mt-8 text-center">
            <span className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border border-red-100">
              Order Cancelled
            </span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">
      <main className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
        <h1 className="text-3xl font-bold mb-6">My Orders</h1>

        {orders.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-xl">No orders found</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(
              orders.reduce((groups, order) => {
                const id = order.checkoutId || order._id;
                if (!groups[id]) groups[id] = [];
                groups[id].push(order);
                return groups;
              }, {})
            ).map(([checkoutId, subOrders]) => {
              const firstOrder = subOrders[0];
              const isGrouped = subOrders.length > 1 || firstOrder.checkoutId;
              const totalAmount = subOrders.reduce((sum, o) => sum + o.finalAmount, 0);
              const allCancelled = subOrders.every(o => o.orderStatus === 'cancelled');

              return (
                <div key={checkoutId} className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                  {/* Card Header */}
                  <div className="bg-gray-50 p-6 border-b border-gray-100 flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-extrabold text-gray-900 group flex items-center gap-2">
                        {isGrouped ? `Order ID #${checkoutId.slice(-6).toUpperCase()}` : `Order #${firstOrder._id.slice(-8).toUpperCase()}`}
                        {allCancelled && (
                          <button
                            onClick={() => handleDeleteOrder(checkoutId)}
                            className="p-2 text-gray-400 hover:text-white hover:bg-red-500 rounded-lg transition-all shadow-sm active:scale-95 ml-2 border border-transparent hover:border-red-600"
                            title="Delete from view"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </h3>
                      <p className="text-gray-500 text-sm mt-1">
                        Placed on {new Date(firstOrder.createdAt).toLocaleDateString(undefined, {
                          year: 'numeric', month: 'long', day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase font-bold tracking-widest text-gray-400 mb-1">Total Amount</p>
                      <p className="text-2xl font-black text-purple-600">₹{totalAmount.toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Items and Sub-orders */}
                  <div className="p-6 space-y-6">
                    {subOrders.map((order, sIdx) => (
                      <div key={order._id} className={`${sIdx !== 0 ? 'border-t pt-6' : ''}`}>
                        <div className="flex justify-between items-center mb-4">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-tighter">Vendor:</span>
                            <span className="text-sm font-semibold text-gray-700">
                              {order.resellerId?.storeName || (order.items?.[0]?.title?.includes('Mock') ? 'Test Reseller' : 'Marketplace')}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(order.orderStatus)}`}>
                              Status: {order.orderStatus}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getPaymentStatusColor(order.paymentStatus)}`}>
                              Payment: {order.paymentStatus}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-4">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-4 group">
                              <div className="relative">
                                <img
                                  src={item.image || '/placeholder.png'}
                                  alt={item.title}
                                  className="w-20 h-20 object-cover rounded-xl shadow-sm group-hover:shadow-md transition-shadow"
                                />
                                <span className="absolute -top-2 -right-2 bg-purple-600 text-white text-[10px] font-bold w-6 h-6 flex items-center justify-center rounded-full border-2 border-white">
                                  {item.quantity}
                                </span>
                              </div>
                              <div className="flex-1">
                                <h4 className="font-bold text-gray-900 group-hover:text-purple-600 transition-colors">{item.title}</h4>
                                <div className="flex items-baseline gap-2 mt-1">
                                  <p className="text-sm font-semibold text-gray-500">
                                    ₹{item.price.toLocaleString()} per unit
                                  </p>
                                  {item.discount > 0 && (
                                    <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-bold uppercase">
                                      {item.discount}% OFF
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-gray-700">₹{Math.round((item.price * (1 - item.discount / 100)) * item.quantity).toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Order Actions for this sub-order */}
                        <div className="mt-6 flex flex-wrap gap-3">
                          {order.orderStatus !== 'cancelled' && (
                            <button
                              onClick={() => setActiveTrackingId(activeTrackingId === order._id ? null : order._id)}
                              className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${
                                activeTrackingId === order._id 
                                ? 'bg-purple-600 text-white shadow-md shadow-purple-200' 
                                : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                              }`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                              </svg>
                              {activeTrackingId === order._id ? 'Close Tracking' : 'Track Order'}
                            </button>
                          )}

                          {order.orderStatus === 'pending' && (
                            <button
                              onClick={() => handleCancelOrder(order._id)}
                              className="px-4 py-2 border border-red-100 text-red-600 rounded-lg text-sm font-bold hover:bg-red-50 transition-colors"
                            >
                              Cancel Order
                            </button>
                          )}

                          {order.orderStatus === 'shipped' && (
                            <button
                              onClick={() => handleMarkDelivered(order._id)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 transition-colors shadow-sm"
                            >
                              Mark as Delivered
                            </button>
                          )}

                          {order.isAwaitingReceipt && order.paymentStatus === 'pending' && (
                            <div className="w-full mt-4 bg-orange-50 p-4 rounded-xl border border-orange-100 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">🧾</span>
                                <div>
                                  <p className="text-sm font-bold text-orange-800">Action Required: Upload Receipt</p>
                                  <p className="text-xs text-orange-600">Please provide a screenshot of your payment.</p>
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedOrderId(order._id);
                                  setShowReceiptModal(true);
                                }}
                                className="px-5 py-2 bg-orange-600 text-white rounded-lg text-sm font-bold hover:bg-orange-700 transition-all shadow-sm active:scale-95"
                              >
                                Upload Now
                              </button>
                            </div>
                          )}

                          {order.receiptImage && (
                            <div className="w-full mt-4 bg-green-50 p-3 rounded-lg border border-green-100 flex items-center gap-3">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600">✓</div>
                              <div className="flex-1">
                                <p className="text-xs font-bold text-green-800 uppercase tracking-wider">Receipt Submitted</p>
                                <a href={order.receiptImage} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-[10px] font-bold hover:underline">View Uploaded Image</a>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Delivery Info & OTP */}
                        <div className="space-y-4">
                          {order.orderStatus === 'shipped' && (
                            <div className="mt-4 bg-purple-50 p-4 rounded-xl border border-purple-100 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">🔑</span>
                                <div>
                                  <p className="text-xs font-bold text-purple-800 uppercase tracking-wider">Delivery Security Code</p>
                                  <p className="text-xs text-purple-600">Share this with the delivery agent only.</p>
                                </div>
                              </div>
                              <span className="text-2xl font-black font-mono tracking-[0.2em] text-purple-700 bg-white px-4 py-1 rounded-lg border border-purple-200">
                                {order.deliveryOtp || '----'}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Tracking Details View */}
                        {activeTrackingId === order._id && (
                          <div className="mt-8 mb-6 p-6 bg-gray-50 rounded-2xl border border-gray-100 animate-in slide-in-from-top-2 duration-300">
                            <h5 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-8 px-1">Shipment Progress</h5>
                            <TrackingProgress status={order.orderStatus} />
                            
                            {(order.shippingProvider || order.trackingNumber) && (
                              <div className="mt-12 pt-6 border-t border-gray-200 flex flex-wrap gap-6">
                                {order.shippingProvider && (
                                  <div>
                                    <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1">Carrier</p>
                                    <p className="text-sm font-bold text-gray-900">{order.shippingProvider}</p>
                                  </div>
                                )}
                                {order.trackingNumber && (
                                  <div>
                                    <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1">Tracking ID</p>
                                    <p className="text-sm font-mono font-bold text-purple-600">{order.trackingNumber}</p>
                                  </div>
                                )}
                                {order.estimatedDeliveryDate && (
                                  <div>
                                    <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1">Estimated Arrival</p>
                                    <p className="text-sm font-bold text-gray-900">
                                      {new Date(order.estimatedDeliveryDate).toLocaleDateString(undefined, {
                                        month: 'short', day: 'numeric', year: 'numeric'
                                      })}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {order.orderStatus === 'shipped' && (
                          <div className="mt-4 bg-purple-50 p-4 rounded-xl border border-purple-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">🔑</span>
                              <div>
                                <p className="text-xs font-bold text-purple-800 uppercase tracking-wider">Delivery Security Code</p>
                                <p className="text-xs text-purple-600">Share this with the delivery agent only.</p>
                              </div>
                            </div>
                            <span className="text-2xl font-black font-mono tracking-[0.2em] text-purple-700 bg-white px-4 py-1 rounded-lg border border-purple-200">
                              {order.deliveryOtp || '----'}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Receipt Upload Modal */}
      {showReceiptModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 animate-in fade-in zoom-in duration-300">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🧾</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 leading-tight">Upload Payment Receipt</h3>
              <p className="text-gray-500 mt-2 text-sm px-4">
                Please provide a clear screenshot of your bank transfer or UPI payment confirmation.
              </p>
            </div>

            <div className="relative group">
              <input
                type="file"
                id="receipt-file"
                className="hidden"
                accept="image/*"
                onChange={handleReceiptUpload}
                disabled={uploadingReceipt}
              />
              <label
                htmlFor="receipt-file"
                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-10 transition-all cursor-pointer ${uploadingReceipt
                    ? 'border-gray-200 bg-gray-50'
                    : 'border-purple-200 bg-purple-50 group-hover:border-purple-400 group-hover:bg-purple-100'
                  }`}
              >
                {uploadingReceipt ? (
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mb-2"></div>
                    <span className="text-sm font-medium text-purple-600">Uploading...</span>
                  </div>
                ) : (
                  <>
                    <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">📸</div>
                    <span className="text-purple-600 font-bold text-lg mb-1">Select Receipt Image</span>
                    <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Supports JPG, PNG up to 2MB</p>
                  </>
                )}
              </label>
            </div>

            <button
              onClick={() => setShowReceiptModal(false)}
              disabled={uploadingReceipt}
              className="w-full mt-8 py-3 bg-white text-gray-500 font-bold rounded-xl border-2 border-gray-100 hover:bg-gray-50 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        variant={confirmConfig.variant}
      />
    </div>
  );
};

export default OrderTracking;

